import { DialogButton, DialogCloseButton, DialogHeader, DialogTitle } from '../../Dialog'
import { Box, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material'
import CheckCircle from '../../../icons/CheckCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { MoreHorizIcon } from '../../../assets/icons'
import { FileViewerMenu } from './FileViewerMenu'
import { EditField } from './EditField'
import bytesToSize from '../../../utils/bytesToSize'
import React, { useCallback, useEffect, useState } from 'react'
import { FileViewerMenuAction } from './FileViewerDialog'
import { trpc } from '../../../core/trpc'
import { toast } from 'react-hot-toast'
import { FileOutput } from '../../../types/procedureOutputs'
import { getFileNameExtension, stripFileNameExt } from '../../../utils/helpers'

const MONTHS_LIST = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const FILE_TYPES = [
  'Application',
  'AR Report',
  'Bank Statement',
  'Credit Card Statement',
  'Debt sheet',
  'Tax Return',
  'Contract File',
  'Driver License',
  'Voided Check'
]

export interface FileViewerSidebarProps {
  /**
   * Actions to be displayed in the file viewer menu
   */
  actions: FileViewerMenuAction[]

  /**
   * Callback to be called when an action is selected in the file viewer menu
   * @param action
   */
  onSelectAction: (action: FileViewerMenuAction) => void

  onClose: () => void
  onFileUpdate: () => void
  hasPrevFile: boolean
  onPrevFile: () => void
  hasNextFile: boolean
  onNextFile: () => void

  file: FileOutput
}

export const SystemFileSidebar = (props: FileViewerSidebarProps) => {
  const { onSelectAction, onClose, hasPrevFile, onPrevFile, hasNextFile, onNextFile, file, onFileUpdate } = props

  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedFileType, setSelectedFileType] = useState<string>('')
  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const [isEditingFilename, setIsEditingFilename] = useState(false)
  const [filename, setFilename] = useState<string>(file!.name ?? '')
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [description, setDescription] = useState<string>(file?.description ?? '')

  const updateFileMutation = trpc.file.updateFile.useMutation({
    onSuccess: (data, variables, context) => {
      toast.success('Successfully updated file')

      onFileUpdate()
    },
    onError: () => toast.error('File update failed')
  })

  const inferFileTypeAndMonth = useCallback((filename: string) => {
    const inferredFileType = FILE_TYPES.find((type) => filename.toLowerCase().includes(type.toLowerCase())) ?? ''
    const inferredMonth = MONTHS_LIST.find((month) => filename.toLowerCase().includes(month.toLowerCase())) ?? ''
    return { inferredFileType, inferredMonth }
  }, [])

  useEffect(() => {
    if (!file) return

    const { inferredFileType, inferredMonth } = inferFileTypeAndMonth(file.name)
    setSelectedFileType(inferredFileType)
    setSelectedMonth(inferredMonth)
    setFilename(file.name)
    setDescription(file.description ?? '')
    setIsEditingFilename(false)
    setIsEditingDescription(false)
  }, [file])

  const updateFilename = useCallback((name: string, newFileType: string, newMonth: string) => {
    const parts = name.split(/\s*-\s*/).filter(Boolean)

    let baseName = parts.slice(0, -1).join(' - ')
    let extension = parts[parts.length - 1].split('.').pop() ?? ''

    if (!parts[parts.length - 1].includes('.')) {
      baseName = parts.join(' - ')
      extension = ''
    }

    const filteredParts = baseName
      .split(' - ')
      .filter(
        (part) =>
          !FILE_TYPES.some((type) => part.toLowerCase().includes(type.toLowerCase())) &&
          !MONTHS_LIST.some((month) => part.toLowerCase().includes(month.toLowerCase()))
      )

    if (newFileType) filteredParts.unshift(newFileType.trim())
    if (newMonth) filteredParts.push(newMonth.trim())
    const filename = filteredParts.join(' - ')
    return extension ? `${filename}.${extension}` : filename
  }, [])

  const handleSelectFileType = (e: SelectChangeEvent<string>) => {
    const newFileType = e.target.value
    setSelectedFileType(newFileType)
    const newFilename = updateFilename(filename, newFileType, selectedMonth)
    setFilename(newFilename)
    setIsEditingFilename(true)
  }

  const handleSelectMonth = (e: SelectChangeEvent<string>) => {
    const newMonth = e.target.value
    setSelectedMonth(newMonth)
    const newFilename = updateFilename(filename, selectedFileType, newMonth)
    setFilename(newFilename)
    setIsEditingFilename(true)
  }

  const handleCancelEditFilename = () => {
    setIsEditingFilename(false)
    setFilename(file!.name)
    const { inferredFileType, inferredMonth } = inferFileTypeAndMonth(file!.name)
    setSelectedFileType(inferredFileType)
    setSelectedMonth(inferredMonth)
  }

  const handleSaveEditFilename = async () => {
    const originalExtension = getFileNameExtension(file!.name)
    const baseName = stripFileNameExt(filename)
    const filenameWithExtension = baseName + originalExtension

    updateFileMutation.mutate(
      { id: file!.id, name: filenameWithExtension },
      {
        onSuccess: () => {
          setFilename(filename)
          setIsEditingFilename(false)
          const { inferredFileType, inferredMonth } = inferFileTypeAndMonth(filename)
          setSelectedFileType(inferredFileType)
          setSelectedMonth(inferredMonth)
        },
        onError: () => {
          setFilename(file!.name)
          const { inferredFileType, inferredMonth } = inferFileTypeAndMonth(file!.name)
          setSelectedFileType(inferredFileType)
          setSelectedMonth(inferredMonth)
        }
      }
    )
  }

  const handleSaveEditDescription = async () => {
    updateFileMutation.mutate({ id: file!.id, description })
    setIsEditingDescription(false)
  }

  return (
    <Box className="flex flex-col flex-grow max-w-[480px]">
      <DialogHeader
        leadingAccessory={
          <DialogTitle
            title={
              <div className="flex items-center">
                {file!.is_contract && (
                  <Tooltip title="Contract File">
                    <CheckCircle color="success" fontSize="small" className="mr-2" />
                  </Tooltip>
                )}
                <span className="truncate max-w-[250px]">{filename}</span>
              </div>
            }
          />
        }
        trailingAccessory={
          <>
            <DialogButton onClick={onPrevFile} disabled={!hasPrevFile} Icon={ArrowBackIcon} />
            <DialogButton onClick={onNextFile} disabled={!hasNextFile} Icon={ArrowForwardIcon} />
            <DialogButton onClick={(e) => setOptionsAnchorEl(e.currentTarget)} Icon={MoreHorizIcon} />

            <DialogCloseButton onClick={onClose} />
            <FileViewerMenu
              actions={[FileViewerMenuAction.Download, FileViewerMenuAction.MarkAsContract, FileViewerMenuAction.Delete]}
              isContract={file.is_contract}
              onClose={() => setOptionsAnchorEl(null)}
              onSelectAction={onSelectAction}
              anchorElement={optionsAnchorEl}
            />
          </>
        }
      />

      <Box className="p-5 pt-0">
        <Box className="flex flex-row gap-4 mb-4">
          <Select
            value={selectedFileType}
            onChange={handleSelectFileType}
            fullWidth
            size="small"
            renderValue={(v) => (v != '' ? v : 'Choose a file type')}
            displayEmpty
          >
            {FILE_TYPES.map((opt) => (
              <MenuItem onClick={(e) => e.stopPropagation()} value={opt} key={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={selectedMonth}
            onChange={handleSelectMonth}
            fullWidth
            size="small"
            renderValue={(v) => (v != '' ? v : 'Choose a month')}
            displayEmpty
          >
            {MONTHS_LIST.map((month) => (
              <MenuItem onClick={(e) => e.stopPropagation()} value={month} key={month}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <EditField
          label="Filename"
          placeholder="Select a file type and month"
          value={filename}
          isEditMode={isEditingFilename}
          editValue={filename}
          onEditValueChange={setFilename}
          onEditMode={() => setIsEditingFilename(true)}
          onCancelEdit={handleCancelEditFilename}
          onSaveEdit={handleSaveEditFilename}
        />

        <EditField
          label="Description"
          placeholder="Enter a description"
          value={file!.description ?? ''}
          isEditMode={isEditingDescription}
          editValue={description}
          onEditValueChange={setDescription}
          onEditMode={() => setIsEditingDescription(true)}
          onCancelEdit={() => setIsEditingDescription(false)}
          onSaveEdit={handleSaveEditDescription}
        />

        {file?.size && <EditField label="File Size" value={bytesToSize(file!.size)} disabled />}
        {file?.created_by?.name && <EditField label="Created by" value={file?.created_by?.name || ''} disabled />}
      </Box>

      <Box className="flex flex-grow bg-grey-100 border-0 border-t border-grey-300 border-solid">{/* placeholder for AI/Copilot */}</Box>
    </Box>
  )
}
